import React, { useState } from "react";
import { ArrowRight, Check, CalendarClock } from "lucide-react";
import Reveal from "./Reveal";
import { FORM_OPTIONS, CALENDLY_URL } from "../mock";

const Field = ({ label, children }) => (
  <label style={{ display: "block" }}>
    <span className="eyebrow" style={{ display: "block", marginBottom: 8 }}>{label}</span>
    {children}
  </label>
);

const inputStyle = {
  width: "100%",
  background: "var(--card)",
  border: "1px solid var(--line-strong)",
  borderRadius: 3,
  padding: "12px 14px",
  fontSize: 15,
  color: "var(--ink)",
  outline: "none",
  transition: "border-color 0.2s ease, box-shadow 0.2s ease",
};

export default function ApplyForm() {
  const [form, setForm] = useState({ name: "", email: "", website: "", sell: "", deal: "", budget: "", meetings: "", start: "", markets: [] });
  const [submitted, setSubmitted] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleMarket = (m) =>
    setForm((f) => ({ ...f, markets: f.markets.includes(m) ? f.markets.filter((x) => x !== m) : [...f.markets, m] }));

  const focus = (e) => { e.target.style.borderColor = "var(--accent)"; e.target.style.boxShadow = "0 0 0 3px rgba(177,68,42,0.12)"; };
  const blur = (e) => { e.target.style.borderColor = "var(--line-strong)"; e.target.style.boxShadow = "none"; };

  const submit = (e) => {
    e.preventDefault();
    try { localStorage.setItem("conduit_application", JSON.stringify({ ...form, at: Date.now() })); } catch (_) {}
    setSubmitted(true);
  };

  const Select = ({ k, options }) => (
    <select value={form[k]} onChange={(e) => set(k, e.target.value)} onFocus={focus} onBlur={blur} style={{ ...inputStyle, appearance: "none", cursor: "pointer" }}>
      <option value="">Select</option>
      {options.map((o) => (<option key={o} value={o}>{o}</option>))}
    </select>
  );

  return (
    <section id="apply" style={{ padding: "80px 0 96px" }}>
      <div className="container-x">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="section-label">Apply to work with us</div>
              <h2 className="h-section" style={{ marginTop: 18 }}>
                We take on a few clients at a time. <span className="serif" style={{ fontSize: "1.05em" }}>Let&rsquo;s see if you&rsquo;re one.</span>
              </h2>
              <p style={{ marginTop: 20, fontSize: 16, lineHeight: 1.6, color: "var(--ink-soft)" }}>
                Outbound works when there is a real offer, a clear market and the budget to run it for a full quarter.
                Answer six questions and you&rsquo;ll know in under two minutes whether we&rsquo;re a fit.
              </p>
              <div style={{ marginTop: 28, borderTop: "1px solid var(--line)" }}>
                {[
                  ["Budget", "From $1,750 a month, 3-month minimum"],
                  ["Buyers", "B2B only: companies, not consumers"],
                  ["Follow-up", "Someone on your side takes the meetings"],
                ].map(([k, v]) => (
                  <div key={k} className="flex items-baseline gap-3" style={{ padding: "14px 0", borderBottom: "1px solid var(--line)" }}>
                    <span className="eyebrow" style={{ minWidth: 78 }}>{k}</span>
                    <span style={{ fontSize: 14.5, color: "var(--ink-soft)" }}>{v}</span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={100}>
              {submitted ? (
                <div className="card-line" style={{ borderRadius: 6, padding: "48px 36px", textAlign: "center", minHeight: 420, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                  <span style={{ width: 56, height: 56, borderRadius: 99, background: "var(--green)", display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#fff" }}>
                    <Check size={26} />
                  </span>
                  <h3 style={{ marginTop: 22, fontSize: 26, fontWeight: 600 }}>
                    You&rsquo;re a fit{form.name ? `, ${form.name.split(" ")[0]}` : ""}.
                  </h3>
                  <p style={{ marginTop: 12, fontSize: 15.5, color: "var(--ink-soft)", maxWidth: 400 }}>
                    Based on your answers this looks like a strong match. Grab a time below and we&rsquo;ll walk through
                    your market, targets and first sequences. A copy went to{" "}
                    <strong style={{ color: "var(--ink)" }}>{form.email || "your email"}</strong>.
                  </p>
                  <a
                    href={CALENDLY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary"
                    style={{ marginTop: 26, padding: "13px 24px" }}
                  >
                    <CalendarClock size={17} /> Book your intro call
                  </a>
                  <button className="btn btn-ghost" style={{ marginTop: 12 }} onClick={() => setSubmitted(false)}>Edit answers</button>
                </div>
              ) : (
                <form onSubmit={submit} className="card-line" style={{ borderRadius: 6, padding: "30px 30px" }}>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Your name">
                      <input required value={form.name} onChange={(e) => set("name", e.target.value)} onFocus={focus} onBlur={blur} placeholder="Jane Doe" style={inputStyle} />
                    </Field>
                    <Field label="Work email">
                      <input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} onFocus={focus} onBlur={blur} placeholder="jane@company.com" style={inputStyle} />
                    </Field>
                  </div>

                  <div style={{ marginTop: 16 }}>
                    <Field label="Company website">
                      <input value={form.website} onChange={(e) => set("website", e.target.value)} onFocus={focus} onBlur={blur} placeholder="company.com" style={inputStyle} />
                    </Field>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4" style={{ marginTop: 16 }}>
                    <Field label="What you sell"><Select k="sell" options={FORM_OPTIONS.sell} /></Field>
                    <Field label="Average deal value"><Select k="deal" options={FORM_OPTIONS.deal} /></Field>
                  </div>

                  <div style={{ marginTop: 16 }}>
                    <span className="eyebrow" style={{ display: "block", marginBottom: 10 }}>Target markets</span>
                    <div className="flex flex-wrap gap-2">
                      {FORM_OPTIONS.markets.map((m) => {
                        const on = form.markets.includes(m);
                        return (
                          <button type="button" key={m} onClick={() => toggleMarket(m)} className="btn" style={{ padding: "8px 14px", fontSize: 13.5, background: on ? "var(--ink)" : "transparent", color: on ? "var(--paper)" : "var(--ink)", border: `1px solid ${on ? "var(--ink)" : "var(--line-strong)"}` }}>
                            {on && <Check size={13} />} {m}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4" style={{ marginTop: 16 }}>
                    <Field label="Monthly budget for outbound"><Select k="budget" options={FORM_OPTIONS.budget} /></Field>
                    <Field label="Who takes the meetings?"><Select k="meetings" options={FORM_OPTIONS.meetings} /></Field>
                  </div>

                  <div style={{ marginTop: 16 }}>
                    <Field label="When do you want to start?"><Select k="start" options={FORM_OPTIONS.start} /></Field>
                  </div>

                  <button type="submit" className="btn btn-primary" style={{ marginTop: 24, width: "100%", justifyContent: "center" }}>
                    Check fit <ArrowRight size={16} />
                  </button>
                  <p style={{ marginTop: 14, fontSize: 12.5, color: "var(--ink-faint)", textAlign: "center" }}>
                    No spam. Your answers are only used to assess fit.
                  </p>
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
